import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { g, p, pa, eur, errMsg, beep } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { sizeWidth } from "@/pages/Piantina";
import { ChiamateTavolo } from "@/components/ChiamateTavolo";
import { Users, Link2, Unlink, List, Map, Clock, Coffee } from "lucide-react";

const COLOR = {
  libero: "border-emerald-500 bg-emerald-50",
  occupato: "border-rose-500 bg-rose-50",
  attesa_conto: "border-amber-500 bg-amber-50",
};
const LABEL = { libero: "s.free", occupato: "s.busy", attesa_conto: "s.waitBill" };

export default function Sala() {
  const { user, zones, sound, demoMode } = useApp();
  const { t } = useI18n();
  const alerted = useRef({});
  const nav = useNavigate();
  const [tables, setTables] = useState([]);
  const [zone, setZone] = useState(null);
  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "lista" : "piantina"));
  const [mergeMode, setMergeMode] = useState(false);
  const [sel, setSel] = useState([]);
  const [mergeName, setMergeName] = useState("Tavolata 1");
  const [openTable, setOpenTable] = useState(null);
  const [candidati, setCandidati] = useState(null);
  const [covers, setCovers] = useState(2);

  const load = useCallback(() => g("/tables").then(setTables), []);
  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [load]);

  // Avviso sonoro quando un tavolo supera i 10 o i 20 minuti di attesa.
  useEffect(() => {
    // Nella demo i tavoli di prova sono in attesa da ore: gli avvisi coprirebbero lo schermo
    if (demoMode) return;
    tables.forEach((tb) => {
      const w = tb.order?.waiting_minutes || 0;
      const level = w >= 20 ? 2 : w >= 10 ? 1 : 0;
      const prev = alerted.current[tb.id] || 0;
      if (level > prev) {
        alerted.current[tb.id] = level;
        if (sound) beep(level === 1);
        toast.warning(t("s.waitMsg", { t: tb.group_name || tb.number, n: w }));
      }
      if (level === 0 && prev) alerted.current[tb.id] = 0;
    });
  }, [tables, sound, t, demoMode]);

  const late = tables.filter((t) => (t.order?.waiting_minutes || 0) >= 10);

  useEffect(() => {
    if (!zone && zones.length) setZone(zones[0].name);
  }, [zones, zone]);

  const list = tables.filter((t) => t.zone === zone);

  const tap = (t) => {
    if (mergeMode) {
      setSel((s) => (s.includes(t.id) ? s.filter((x) => x !== t.id) : [...s, t.id]));
      return;
    }
    if (t.order) return nav(`/comanda/${t.order.id}`);
    setOpenTable(t);
    setCovers(t.reservation?.people || t.seats);
    setCandidati(null);
    g(`/tables/${t.id}/candidati`).then(setCandidati).catch(() => setCandidati(null));
  };

  const openOrder = async () => {
    try {
      const o = await p("/orders", { table_ids: [openTable.id], covers: Number(covers) || 1, waiter: user.name });
      setOpenTable(null);
      nav(`/comanda/${o.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doMerge = async () => {
    try {
      await p("/tables/merge", { table_ids: sel, name: mergeName, by: user.name });
      toast.success(`${t("s.merged")}: ${mergeName}`);
      setMergeMode(false);
      setSel([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const unmerge = async (gid) => {
    try {
      await p(`/tables/unmerge/${gid}`);
      toast.success(t("s.unmerged"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const free = async (t) => {
    try {
      await pa(`/tables/${t.id}/status`, { status: "libero" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const blink = (t) => {
    const w = t.order?.waiting_minutes || 0;
    // Pulsa solo il bordo: prima lampeggiava tutta la scheda e numeri e scritte sbiadivano
    if (w >= 20) return "tavolo-tardi";
    if (w >= 10) return "tavolo-attesa";
    return "";
  };

  const node = (tb) => {
    const mine = tb.order?.waiter === user.name;
    return (
      <button
        key={tb.id}
        data-testid={`table-${tb.number}`}
        onClick={() => tap(tb)}
        className={`border-2 p-2 text-left transition-transform active:scale-[0.97] sm:p-3 ${
          tb.shape === "round" ? "rounded-2xl" : "rounded-lg"
        } ${COLOR[tb.status]} ${blink(tb)} ${sel.includes(tb.id) ? "ring-4 ring-slate-900" : ""}`}
        style={view === "piantina" ? { position: "absolute", left: `${tb.x}%`, top: `${tb.y}%`, width: `${sizeWidth(tb.size)}%` } : {}}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-head truncate text-sm font-bold sm:text-base">{tb.group_name || tb.number}</span>
          {mine && <span className="rounded-full bg-amber-400 px-1.5 text-[9px] font-bold text-slate-900">{t("s.yours")}</span>}
        </div>
        <p className="mt-0.5 flex items-center gap-1 truncate text-[10px] text-slate-600 sm:text-[11px]">
          <Users className="h-3 w-3 shrink-0" /> {tb.order?.covers || tb.seats} · {t(LABEL[tb.status])}
        </p>
        {tb.reservation && !tb.order && (
          <p className="font-num mt-1 text-[11px] font-semibold text-slate-700" data-testid={`table-res-${tb.number}`}>
            {tb.reservation.in_minutes >= 0 && tb.reservation.in_minutes <= 30
              ? `prenotato tra ${tb.reservation.in_minutes} min`
              : `prenotato ${tb.reservation.time}`} ({tb.reservation.name})
          </p>
        )}
        {tb.order && (
          <p className="font-num mt-1 truncate text-xs font-bold sm:text-sm">
            {eur(tb.order.remaining)}
            {tb.order.waiting_minutes >= 10 && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-medium text-rose-700">
                <Clock className="h-3 w-3" />
                {tb.order.waiting_minutes}′
              </span>
            )}
          </p>
        )}
      </button>
    );
  };

  return (
    <div className="fade-up space-y-5" data-testid="sala-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("s.service")}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("nav.sala")}</h1>
          {late.length > 0 && (
            <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-rose-600" data-testid="late-tables-banner">
              <Clock className="h-3.5 w-3.5" /> {t("s.late", { n: late.length })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant={view === "piantina" ? "default" : "outline"} onClick={() => setView("piantina")} data-testid="view-piantina-btn">
            <Map className="mr-1.5 h-4 w-4" /> {t("s.map")}
          </Button>
          <Button variant={view === "lista" ? "default" : "outline"} onClick={() => setView("lista")} data-testid="view-lista-btn">
            <List className="mr-1.5 h-4 w-4" /> {t("s.list")}
          </Button>
          <Button
            variant="outline"
            data-testid="counter-sale-btn"
            onClick={async () => {
              try {
                const o = await p("/counter", { waiter: user.name });
                toast.success(t("x.counter.ok"), { description: t("x.counter.hint") });
                nav(`/comanda/${o.id}`);
              } catch (e) {
                toast.error(errMsg(e));
              }
            }}
          >
            <Coffee className="mr-1.5 h-4 w-4" /> {t("x.counter.btn")}
          </Button>
          <Button
            variant={mergeMode ? "default" : "outline"}
            data-testid="merge-mode-btn"
            onClick={() => {
              setMergeMode(!mergeMode);
              setSel([]);
            }}
          >
            <Link2 className="mr-1.5 h-4 w-4" /> {t("s.merge")}
          </Button>
        </div>
      </div>

      <ChiamateTavolo />

      <div className="flex flex-wrap gap-2">
        {zones.map((z) => (
          <button
            key={z.id}
            data-testid={`zone-${z.name.toLowerCase()}`}
            onClick={() => setZone(z.name)}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              zone === z.name ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            {z.name}
          </button>
        ))}
      </div>

      {mergeMode && (
        <Card className="flex flex-wrap items-center gap-3 p-4" data-testid="merge-bar">
          <span className="text-sm text-slate-600">{t("s.merge.hint", { n: sel.length })}</span>
          <Input className="h-9 w-40" value={mergeName} onChange={(e) => setMergeName(e.target.value)} data-testid="merge-name-input" />
          <Button onClick={doMerge} disabled={sel.length < 2} data-testid="confirm-merge-btn">
            {t("s.merge.ok")}
          </Button>
        </Card>
      )}

      {view === "piantina" ? (
        <Card className="relative h-[420px] overflow-hidden bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:20px_20px] sm:h-[560px]" data-testid="piantina">
          {list.map(node)}
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5" data-testid="lista-tavoli">
          {list.map(node)}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {[...new Set(list.filter((t) => t.group_id).map((t) => t.group_id))].map((gid) => (
          <Button key={gid} variant="outline" size="sm" data-testid={`unmerge-${gid}`} onClick={() => unmerge(gid)}>
            <Unlink className="mr-1.5 h-3.5 w-3.5" /> {t("s.split")} {list.find((t) => t.group_id === gid)?.group_name}
          </Button>
        ))}
        {list
          .filter((x) => x.status === "attesa_conto" && !x.order)
          .map((tb) => (
            <Button key={tb.id} variant="outline" size="sm" data-testid={`free-table-${tb.number}`} onClick={() => free(tb)}>
              {t("s.freeTable", { n: tb.number })}
            </Button>
          ))}
      </div>

      <Dialog open={!!openTable} onOpenChange={(v) => !v && setOpenTable(null)}>
        <DialogContent data-testid="open-table-dialog">
          <DialogHeader>
            <DialogTitle>{t("s.table")} {openTable?.number} — {t("s.covers")}</DialogTitle>          </DialogHeader>
          {openTable?.reservation && (
            <p className="text-sm text-slate-500">
              {t("s.booked")} {openTable.reservation.time} · {openTable.reservation.name}
            </p>
          )}

          {(candidati?.waitlist?.length > 0 || candidati?.reservations?.length > 0) && (
            <div className="space-y-1.5 rounded-lg bg-slate-50 p-3" data-testid="candidati-tavolo">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                Chi sta aspettando questo tavolo
              </p>
              {candidati.reservations.map((r) => (
                <div key={r.id} className="flex items-center gap-2" data-testid={`cand-pren-${r.id}`}>
                  <span className="min-w-0 flex-1 truncate text-xs">
                    <b>{r.time}</b> {r.customer_name} · {r.people} pax
                    {r.in_minutes >= 0 ? ` · tra ${r.in_minutes} min` : " · in ritardo"}
                    {r.customer_no_shows > 0 && <span className="ml-1 text-rose-600">({r.customer_no_shows} assenze)</span>}
                    {!r.fits && <span className="ml-1 text-amber-700">(tavolo piccolo)</span>}
                  </span>
                  <Button size="sm" className="h-8" data-testid={`cand-pren-seat-${r.id}`}
                          onClick={async () => {
                            try {
                              const res = await p(`/reservations/${r.id}/seat`, { by: user.name, table_ids: [openTable.id] });
                              setOpenTable(null);
                              nav(`/comanda/${res.order_id}`);
                            } catch (e) {
                              toast.error(errMsg(e));
                            }
                          }}>
                    Accomoda
                  </Button>
                </div>
              ))}
              {candidati.waitlist.map((w) => (
                <div key={w.id} className="flex items-center gap-2" data-testid={`cand-attesa-${w.id}`}>
                  <span className="min-w-0 flex-1 truncate text-xs">
                    In attesa · {w.name} · {w.people} pax · da {w.waited_minutes} min
                    {!w.fits && <span className="ml-1 text-amber-700">(tavolo piccolo)</span>}
                  </span>
                  <Button size="sm" variant="outline" className="h-8" data-testid={`cand-attesa-seat-${w.id}`}
                          onClick={async () => {
                            try {
                              await p(`/waitlist/${w.id}/seat`, { table: openTable.number, by: user.name });
                              const o = await p("/orders", { table_ids: [openTable.id], covers: w.people, waiter: user.name });
                              setOpenTable(null);
                              nav(`/comanda/${o.id}`);
                            } catch (e) {
                              toast.error(errMsg(e));
                            }
                          }}>
                    Accomoda
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
              <button
                key={n}
                data-testid={`covers-${n}`}
                onClick={() => setCovers(n)}
                className={`h-11 w-11 rounded-md border text-sm font-bold ${
                  Number(covers) === n ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"
                }`}
              >
                {n}
              </button>
            ))}
            <Input type="number" min="1" value={covers} onChange={(e) => setCovers(e.target.value)} className="h-11 w-20" data-testid="covers-input" />
          </div>
          <DialogFooter>
            <Button className="h-12 w-full" onClick={openOrder} data-testid="open-order-btn">
              {t("s.openOrder")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
